/**
 * @element qds-code-block
 *
 * A reusable code presentation component with a language label and
 * clipboard copy action.
 *
 * @attr {string} language - Language label shown in the header
 *
 * @property {string} value - Code content to render and copy
 *
 * @csspart frame - The outer code block frame
 * @csspart toolbar - The header row containing the language and copy button
 * @csspart language - The language label
 * @csspart copy-button - The copy button
 * @csspart pre - The preformatted content area
 * @csspart code - The code element
 *
 * @fires copied - Fired when the code has been copied to the clipboard
 * @fires copyerror - Fired when copying the code fails
 */

import { loadBaseCSS } from '../shared/button-utils.js';

loadBaseCSS();

export class QdsCodeBlock extends HTMLElement {
  private _language = 'text';
  private _value = '';
  private _copyState: 'copy' | 'copied' | 'error' = 'copy';
  private _resetTimer?: number;
  private _boundCopyHandler: (() => void) | null = null;

  static get observedAttributes(): string[] {
    return ['language'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (!this.isConnected) return;

    if (!this._value) {
      this._value = this.textContent || '';
    }

    this.render();
    this._bindEvents();
  }

  disconnectedCallback(): void {
    this._clearResetTimer();
    this._unbindEvents();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    if (name === 'language') {
      this._language = this._normalizeLanguage(newValue || 'text');
      if (this.isConnected) {
        this.render();
        this._bindEvents();
      }
    }
  }

  get language(): string {
    return this._language;
  }

  set language(value: string) {
    this.setAttribute('language', value);
  }

  get value(): string {
    return this._value;
  }

  set value(value: string) {
    this._value = value || '';
    if (this.isConnected) {
      this.render();
      this._bindEvents();
    }
  }

  private _normalizeLanguage(value: string): string {
    const normalized = value.trim().toLowerCase();
    const aliases: Record<string, string> = {
      js: 'javascript',
      ts: 'typescript',
      md: 'markdown',
      shell: 'bash',
      sh: 'bash',
      yml: 'yaml',
      plaintext: 'text',
      plain: 'text'
    };

    return aliases[normalized] || normalized || 'text';
  }

  private _getLanguageLabel(): string {
    const labels: Record<string, string> = {
      html: 'HTML',
      css: 'CSS',
      javascript: 'JavaScript',
      typescript: 'TypeScript',
      markdown: 'Markdown',
      md: 'Markdown',
      json: 'JSON',
      bash: 'Bash',
      yaml: 'YAML',
      xml: 'XML',
      text: 'Text'
    };

    return labels[this._language] || this._language.toUpperCase();
  }

  private _getCopyButtonLabel(): string {
    switch (this._copyState) {
      case 'copied':
        return 'Copied';
      case 'error':
        return 'Copy failed';
      default:
        return 'Copy';
    }
  }

  private _bindEvents(): void {
    this._unbindEvents();

    const button = this.shadowRoot?.querySelector('.copy-button');
    if (!button) return;

    this._boundCopyHandler = () => {
      void this._handleCopy();
    };

    button.addEventListener('click', this._boundCopyHandler);
  }

  private _unbindEvents(): void {
    const button = this.shadowRoot?.querySelector('.copy-button');
    if (button && this._boundCopyHandler) {
      button.removeEventListener('click', this._boundCopyHandler);
    }

    this._boundCopyHandler = null;
  }

  private _clearResetTimer(): void {
    if (this._resetTimer) {
      window.clearTimeout(this._resetTimer);
      this._resetTimer = undefined;
    }
  }

  private _setCopyState(state: 'copy' | 'copied' | 'error'): void {
    this._copyState = state;

    const button = this.shadowRoot?.querySelector('.copy-button');
    if (button) {
      button.textContent = this._getCopyButtonLabel();
      button.setAttribute('data-state', state);
    }
  }

  private _scheduleReset(): void {
    this._clearResetTimer();
    this._resetTimer = window.setTimeout(() => {
      this._setCopyState('copy');
    }, 2000);
  }

  private async _handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this._value);
      this._setCopyState('copied');
      this._scheduleReset();
      this.dispatchEvent(new CustomEvent('copied', {
        bubbles: true,
        composed: true,
        detail: {
          language: this._language,
          value: this._value
        }
      }));
    } catch (error) {
      console.error('Failed to copy code block:', error);
      this._setCopyState('error');
      this._scheduleReset();
      this.dispatchEvent(new CustomEvent('copyerror', {
        bubbles: true,
        composed: true,
        detail: {
          language: this._language,
          value: this._value,
          error
        }
      }));
    }
  }

  private render(): void {
    if (!this.shadowRoot) return;

    this._language = this._normalizeLanguage(this.getAttribute('language') || this._language);

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          margin: 20px 0;
        }

        .frame {
          border: 1px solid var(--theme-border-primary);
          border-radius: 0;
          background-color: var(--theme-background-primary);
          overflow: hidden;
        }

        .toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          min-height: 44px;
          padding: 0 16px;
          border-bottom: 1px solid var(--theme-border-primary);
          background-color: var(--theme-background-secondary);
        }

        .language {
          color: var(--theme-text-secondary);
          font-family: 'IBM Plex Mono', monospace;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .copy-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 32px;
          padding: 6px 10px;
          border: 0;
          background: transparent;
          color: var(--theme-accent-primary);
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s ease, color 0.2s ease;
        }

        .copy-button:hover {
          background-color: var(--theme-background-hover);
          color: var(--theme-accent-secondary);
        }

        .copy-button:focus-visible {
          outline: 2px solid var(--theme-accent-primary);
          outline-offset: 2px;
        }

        .copy-button[data-state="copied"] {
          color: var(--theme-success);
        }

        .copy-button[data-state="error"] {
          color: var(--theme-error);
        }

        pre {
          margin: 0;
          padding: 16px;
          overflow-x: auto;
          background-color: var(--theme-background-primary);
          color: var(--theme-text-primary);
          font-family: 'IBM Plex Mono', monospace;
          font-size: 14px;
          line-height: 1.6;
          white-space: pre;
          tab-size: 2;
        }

        code {
          display: block;
          background: transparent;
          color: inherit;
          font: inherit;
          white-space: inherit;
        }
      </style>

      <div class="frame" part="frame">
        <div class="toolbar" part="toolbar">
          <span class="language" part="language">${this._getLanguageLabel()}</span>
          <button
            type="button"
            class="copy-button"
            part="copy-button"
            data-state="${this._copyState}"
            aria-label="Copy ${this._getLanguageLabel()} code">
            ${this._getCopyButtonLabel()}
          </button>
        </div>
        <pre part="pre"><code part="code"></code></pre>
      </div>
    `;

    const codeElement = this.shadowRoot.querySelector('code');
    if (codeElement) {
      codeElement.textContent = this._value;
    }
  }
}

if (!customElements.get('qds-code-block')) {
  customElements.define('qds-code-block', QdsCodeBlock);
}
